import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Chip } from '../primitives';
import { colors, iconSize } from '@munifocus/tokens';

export interface TagChipProps {
  label: ReactNode;
  /** Optional leading icon, sized by this component. */
  icon?: LucideIcon;
  /**
   * A tag the deployment chose, as against one that came from a default.
   *
   * The signing-authority and membership tables both needed to say "this one is
   * an explicit decision, that one is inherited". Filled versus outlined is how
   * they said it, and it is the only reason this prop exists — do not reach for
   * it to make a tag louder.
   */
  emphasis?: boolean;
  /** Removes the tag. Renders the × affordance. */
  onDelete?: () => void;
  title?: string;
}

/** Every icon inside a chip: the inline step, sized to the text beside it. */
export const CHIP_ICON_SIZE = iconSize.xs;

/**
 * A factual label: a role, a locale, a document kind, a municipality's name.
 *
 * **Not a status.** The distinction is whether the reader is being asked to
 * judge anything. "Lead officer", "PDF", "Moncton" and "Multiple choice" are
 * facts about a row; "Suspended" and "Expired" are states someone may need to
 * act on. Colouring the first kind spends the reader's attention on nothing,
 * and once tags carry colour, a status chip in the same row stops standing out
 * — which is the whole job of a status chip. Use `StatusChip` for those.
 *
 * Roughly forty of these were written as a bare MUI `<Chip>`, about half of them
 * `variant="outlined"` and half filled, with no rule behind which — the same
 * document kind was an outlined chip on one panel and a filled one on the next.
 */
export function TagChip({ label, icon: Icon, emphasis, onDelete, title }: TagChipProps) {
  return (
    <Chip
      size="small"
      label={label}
      title={title}
      icon={Icon ? <Icon size={CHIP_ICON_SIZE} /> : undefined}
      onDelete={onDelete}
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        maxWidth: '100%',
        backgroundColor: emphasis ? colors.surface.cream2 : 'transparent',
        border: '1px solid',
        borderColor: emphasis ? 'transparent' : colors.border.onCream,
        color: colors.text.secondary,
        '& .MuiChip-icon': { color: 'inherit', marginLeft: '6px' },
      }}
    />
  );
}
